'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';
import { APP_SUBTITLE, APP_TITLE } from '@/constants/branding';
import HoaOneIcon from '@/assets/logos/hoaone-icon.png';
import { NoSsr } from '@/components/core/v2/no-ssr';

const HEIGHT = 36;

type Color = 'dark' | 'light';

export interface LogoProps {
  color?: Color;
  emblem?: boolean;
  height?: number;
  width?: number | 'auto';
}

export function Logo({ color = 'dark', height = HEIGHT }: LogoProps): React.JSX.Element {
  const textColor = color === 'light' ? '#ffffff' : '#0a1f44';

  return (
    <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 1 }}>
      <Box
        alt="HOAONE"
        component="img"
        src={HoaOneIcon}
        sx={{ borderRadius: '6px', display: 'block', height: `${height}px`, width: `${height}px` }}
      />
      <Typography
        sx={{
          color: textColor,
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: `${Math.round(height * 0.55)}px`,
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: 1,
        }}
      >
        {APP_TITLE}
      </Typography>
    </Box>
  );
}

export interface DynamicLogoProps {
  colorDark?: Color;
  colorLight?: Color;
  emblem?: boolean;
  height?: number;
  width?: number;
}

export function DynamicLogo({
  colorDark = 'light',
  colorLight = 'dark',
  height = HEIGHT,
  ...props
}: DynamicLogoProps): React.JSX.Element {
  const { colorScheme } = useColorScheme();
  const color = colorScheme === 'dark' ? colorDark : colorLight;

  return (
    <NoSsr fallback={<Box sx={{ height: `${height}px`, width: '120px' }} />}>
      <Logo color={color} height={height} {...props} />
    </NoSsr>
  );
}

export interface LogoWithBrandingProps {
  showSubtitle?: boolean;
  logoHeight?: number;
  /** Show the navy background pill around the logo. Use true only on dark backgrounds (splash). */
  showBackground?: boolean;
}

export function LogoWithBranding({ showSubtitle = true, logoHeight = 48, showBackground = false }: LogoWithBrandingProps): React.JSX.Element {
  return (
    <Stack alignItems="center" spacing={1}>
      {showBackground ? (
        <Box
          sx={{
            bgcolor: 'primary.dark',
            borderRadius: 1.5,
            display: 'inline-flex',
            px: 2,
            py: 1,
          }}
        >
          <DynamicLogo colorDark="light" colorLight="light" height={logoHeight} />
        </Box>
      ) : (
        <DynamicLogo height={logoHeight} />
      )}
      {showSubtitle ? (
        <Typography color="text.secondary" variant="body2">
          {APP_SUBTITLE}
        </Typography>
      ) : null}
    </Stack>
  );
}
